import React, { useCallback, useState } from 'react';

import {
  Block,
  CredentialLookup,
  Recommendation,
  SubBlockType,
  Videos,
} from '@common/@types/recommendations';
import CopyAll from '@mui/icons-material/CopyAll';
import {
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import Paper from '@mui/material/Paper';

import Iconify from '@common/components/Iconify';
import BasicModal from '@common/components/modal/BasicModal';

import {
  StudentProgressView,
  type StudentWeeklyStats,
  type SubjectLevelPersonalGoal,
} from '@common/components/timeback';
import TableContainer from '@mui/material/TableContainer';
import { Role } from 'common/@types/auth';
import { amplitudeService, getCampusTypeByDomain } from 'common/analytics/amplitude';
import useAuth from 'common/hooks/useAuth';
import useCollapseDrawer from 'common/hooks/useCollapseDrawer';
import useModal from 'common/hooks/useModal';
import useSiteConfig from 'common/hooks/useSiteConfig';
import { useStudentProgressSummary } from 'common/hooks/useStudentProgressSummary';
import { AppSubjectGradeThreshold } from 'common/utils/amplify/getMasteryThresholds';
import {
  checkIfEmbedableLink,
  generateEmbedLink,
  getContentType,
} from 'common/utils/contentTypeParser';
import { trpc } from 'common/utils/trpcAmplify';
import { useSnackbar } from 'notistack';
import AccordionProgressWrapper from './AccordianProgressWrapper';
import AccordionListingContainer from './AccordionListingContainer';
import CustomContentTypeIcon from './CustomContentTypeIcon';
import RecommendationTitleSummary from './RecommendationTitleSummary';

const showManualUrl = false;

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

const RecommendationLink = ({
  sso_url,
  manual_url,
  text,
  app,
  subject,
  completed,
  credentialLookup,
  videos,
  onClick,
}: {
  sso_url: string;
  manual_url: string;
  text: string;
  app: string;
  subject: string;
  completed: boolean;
  credentialLookup?: CredentialLookup;
  videos?: Videos;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const [modalsOpen, setModalsOpen] = useState({
    credentialModal: false,
    videoModal: false,
  });
  const handleOpenModal = (modalName: string) => {
    setModalsOpen({ ...modalsOpen, [modalName]: true });
  };
  const handleCloseModal = () => {
    setModalsOpen({
      credentialModal: false,
      videoModal: false,
    });
  };
  const color = completed
    ? theme.palette.text.disabled
    : theme.palette.subjectText[subject as keyof typeof theme.palette.subjectText];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: '2em', // to make sure things with credential icons and without are the same height
      }}
    >
      {sso_url ? (
        <Link onClick={onClick} href={sso_url} target="_blank" variant="body2" color={color}>
          {text}
        </Link>
      ) : (
        <Typography fontSize={'14px'} fontWeight={'600'}>
          {text}
        </Typography>
      )}

      {showManualUrl && manual_url ? (
        <Box sx={{ marginX: '12px', paddingX: '0px' }}>
          <Link onClick={onClick} href={manual_url} target="_blank" variant="caption" color={color}>
            (Manual login)
          </Link>
        </Box>
      ) : null}

      {app && credentialLookup && app in credentialLookup ? (
        <Box>
          <Button
            onClick={() => handleOpenModal('credentialModal')}
            sx={{ paddingX: '0px', minWidth: '32px' }}
          >
            <Iconify icon={'material-symbols:key'} color={color} width={24} height={24} />
          </Button>

          <BasicModal open={modalsOpen.credentialModal} onClose={handleCloseModal}>
            <Button
              onClick={handleCloseModal}
              sx={{ position: 'absolute', paddingY: '12px', top: '0px', right: '0px' }}
            >
              <Iconify icon={'mdi:close'} color={'#41B8D4'} width={24} height={24} />
            </Button>
            <Typography variant={'h4'}>Credentials for {app}</Typography> <br />
            <Typography variant={'body1'} align={'center'}>
              <strong>Username:</strong> <br />
              {credentialLookup[app].credentials.username}
              <Tooltip title="Copy to clipboard">
                <IconButton
                  onClick={() => copyToClipboard(credentialLookup[app].credentials.username)}
                >
                  <CopyAll />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant={'body1'} align={'center'}>
              <strong>Password:</strong> <br />
              {credentialLookup[app].credentials.password}
              <Tooltip title="Copy to clipboard">
                <IconButton
                  onClick={() => copyToClipboard(credentialLookup[app].credentials.password)}
                >
                  <CopyAll />
                </IconButton>
              </Tooltip>
            </Typography>
            {credentialLookup[app].metadata &&
              credentialLookup[app]
                .metadata!.split('|')
                .map((metadataItem: string, index: number) => {
                  const [metadataKey, metadataValue] = metadataItem.split('=');
                  return (
                    <Typography key={index} variant={'body1'} align={'center'}>
                      <strong>{metadataKey}:</strong> <br />
                      {metadataValue}
                      <Tooltip title="Copy to clipboard">
                        <IconButton onClick={() => copyToClipboard(metadataValue)}>
                          <CopyAll />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  );
                })}
          </BasicModal>
        </Box>
      ) : null}
      {app && videos && app in videos ? (
        <Box>
          <Button
            onClick={() => handleOpenModal('videoModal')}
            sx={{ paddingX: '0px', minWidth: '32px' }}
          >
            <Iconify
              icon={'material-symbols:video-library-rounded'}
              color={color}
              width={24}
              height={24}
            />
          </Button>

          <BasicModal open={modalsOpen.videoModal} onClose={handleCloseModal}>
            <Button
              onClick={handleCloseModal}
              sx={{ position: 'absolute', paddingY: '12px', top: '0px', right: '0px' }}
            >
              <Iconify icon={'mdi:close'} color={'#41B8D4'} width={24} height={24} />
            </Button>
            <Typography variant={'h4'}>How to use {app} effectively</Typography> <br />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <iframe
                width="560"
                height="315"
                src={`${videos[app]}`}
                title={`How to use ${app} effectively`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </BasicModal>
        </Box>
      ) : null}
    </Box>
  );
};

const SubjectTitleContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: '20px',
  marginBottom: '20px',
  marginTop: '20px',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  },
}));

const DescriptionContainerDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
  fontSize: '14px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '13px',
  },
}));

type RecommendationBlockProps = {
  masteryThreshold: AppSubjectGradeThreshold | undefined;
  studentLearningBlock: Block;
  credentialLookup: CredentialLookup;
  visibleRows: number | undefined;
  blockIdx: number;
  videos: Videos;
  studentWeeklyStats: StudentWeeklyStats;
  studentPersonalGoal: SubjectLevelPersonalGoal;
  full_student_id: string;
};

const RecommendationBlock = ({
  masteryThreshold,
  studentLearningBlock,
  credentialLookup,
  visibleRows = 5,
  blockIdx,
  videos,
  studentWeeklyStats,
  studentPersonalGoal,
  full_student_id,
}: RecommendationBlockProps) => {
  const [campus_id, student_id] = full_student_id?.split('-');
  const { handleShowVideo, setSkillUrl } = useModal();
  const { collapseClick } = useCollapseDrawer();
  const theme = useTheme();
  const siteConfig = useSiteConfig();
  const { user } = useAuth();
  const isStudent = user?.alphaAuthAttributes?.role === Role.STUDENT;
  const { enqueueSnackbar } = useSnackbar();
  const isUserLearnAndEarn = ['4', '9'].includes(user?.alphaAuthAttributes?.campusId ?? '');
  // Grade is hidden for irvington campus
  const isGradeHidden = [18].includes(parseInt(campus_id ?? '0'));
  const [testOutInProgress, setTestOutInProgress] = useState(false);
  if (!studentLearningBlock.isEligibleForTest && testOutInProgress) {
    setTestOutInProgress(false);
  }
  const goalSummary = useStudentProgressSummary({
    target: masteryThreshold?.target_mastery_units,
    data: studentWeeklyStats[studentLearningBlock.subject],
    personalGoal: studentPersonalGoal[studentLearningBlock.subject],
    isUserLearnAndEarn,
  });
  const [isClickedMap, setIsClickedMap] = useState<Record<string, boolean>>({});

  // Hardcoded way to force test before skill for now
  studentLearningBlock.subBlocks.sort((a, b) => {
    if (a.type === SubBlockType.TEST && b.type === SubBlockType.LEARN) return -1;
    if (a.type === SubBlockType.LEARN && b.type === SubBlockType.TEST) return 1;
    return 0;
  });

  const onClickTestOut = async (subject: string) => {
    const studentId = user?.alphaAuthAttributes?.studentId;
    if (studentId && isStudent) {
      const mutation = trpc.voluntaryTestOut.post.useMutation();
      try {
        setTestOutInProgress(true);
        enqueueSnackbar(`Processing test out request for ${subject}`, {
          variant: 'info',
        });
        const result = await mutation.mutate({
          studentId,
          subject,
        });
        console.log({ result });
        enqueueSnackbar(`${subject} test has been assigned!`, {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);
        setTestOutInProgress(false);
        enqueueSnackbar(`Error processing test out request for ${subject}`, {
          variant: 'error',
        });
      }
    }
  };

  const onClickSkillLink = (linkText: string, row: Recommendation) => {
    if (siteConfig?.data?.domain) {
      const campus = getCampusTypeByDomain(siteConfig.data.domain);
      amplitudeService.trackClickLearningLink(
        campus,
        'Skill',
        linkText,
        row.grade,
        row.recommendation_source,
        row.block,
        row.smart_score,
        row.suggested_content
      );
    }
  };

  const onClickSuggestedLink = (linkText: string, row: Recommendation) => {
    if (siteConfig?.data?.domain) {
      const campus = getCampusTypeByDomain(siteConfig.data.domain);
      amplitudeService.trackClickLearningLink(
        campus,
        'Suggested Content',
        linkText,
        row.grade,
        row.recommendation_source,
        row.block,
        row.smart_score,
        row.suggested_content
      );
    }
  };
  const renderUrlContent = useCallback(
    (row: Recommendation, field: 'suggested_content' | 'url') => {
      let renderedContent;
      const subject =
        studentLearningBlock.subject.toLowerCase() as keyof typeof theme.palette.subjectText;
      const completed = row.completed_on != null;
      // Convert a string with embbed links into alternating Typography and Link components
      if (field === 'suggested_content' && campus_id !== '018') {
        const linkDivider = `###`;
        const linkBlocks: string[] = []; // list of the link blocks to parse

        // Replace all links with a placeholder
        const baseString = row[field]?.replace(/\[.*?\|http[^\s]+\]/g, (match: string) => {
          const idx = linkBlocks.push(match);
          return `${linkDivider}link${idx - 1}${linkDivider}`;
        });

        // Convert the linkBlocks to Link objects
        const linkObjs: { renderLink: React.ReactElement; contentType: string }[] = linkBlocks.map(
          (linkBlock, idx) => {
            let [linkText, linkHref] = linkBlock.replace(/[\[\]]/g, '').split('|');
            if (linkHref.includes('alphatx') && campus_id === '018') {
              linkHref = linkHref.replace('alphatx', 'irvington');
            }
            const isClicked = isClickedMap[linkHref];
            const contentType = getContentType(linkHref);
            let isEmbeddable = checkIfEmbedableLink(linkHref);
            let isHtmlRender = contentType === 'Supporting';
            let embedLink = '';
            if (isEmbeddable) {
              embedLink = generateEmbedLink(linkHref);
            }
            const linkHastag = isEmbeddable ? '#video' : '#html';
            return {
              renderLink: (
                <Link
                  key={'link' + idx}
                  href={isEmbeddable || isHtmlRender ? linkHastag : linkHref}
                  target={isEmbeddable || isHtmlRender ? '_self' : '_blank'}
                  variant="body2"
                  color={
                    completed || isClicked
                      ? theme.palette.text.disabled
                      : theme.palette.subjectText[subject]
                  }
                  onClick={() => {
                    if (isHtmlRender) {
                      setSkillUrl(`${linkHref}?student_id=${student_id}`);
                    }
                    if (isEmbeddable) handleShowVideo(embedLink);
                    onClickSuggestedLink(linkText, row);
                    setIsClickedMap({ ...isClickedMap, [linkHref]: true });
                  }}
                >
                  {linkText}
                </Link>
              ),
              contentType,
            };
          }
        );

        // Split the string
        const splitString = baseString.split(new RegExp(linkDivider, 'gm'));
        // Put Typography components in and collate linkObjs in
        const reactEls: React.ReactElement[] = splitString
          .filter((str) => str.match(/link\d+/))
          .map((str: string, idx: number) => {
            const linkIdx = parseInt(str.replace('link', ''));
            return (
              <DescriptionContainerDiv>
                <CustomContentTypeIcon type={linkObjs[linkIdx].contentType} />
                <span style={{ fontWeight: 600 }}>{linkObjs[linkIdx].contentType} Content - </span>
                {linkObjs[linkIdx].renderLink}
              </DescriptionContainerDiv>
            );
          });
        renderedContent = <>{reactEls}</>;
      }
      // Skill name should point to link
      else if (field === 'url') {
        renderedContent = (
          <RecommendationLink
            credentialLookup={credentialLookup}
            sso_url={row.sso_url}
            manual_url={row.url}
            app={row.application}
            subject={subject}
            completed={completed || isClickedMap[row.url]}
            text={row.name}
            videos={videos}
            onClick={() => {
              onClickSkillLink(row[field], row);
              setIsClickedMap({ ...isClickedMap, [row.url]: true });
            }}
          />
        );
      }

      return renderedContent;
    },
    [isClickedMap, credentialLookup, videos]
  );

  const subLessons = studentLearningBlock.subBlocks.map((subBlock, idx) => {
    if (!subBlock.rows || subBlock.rows.length === 0) return;
    if (![SubBlockType.LEARN, SubBlockType.TEST].includes(subBlock.type)) {
      console.warn(`Unknown subBlock type found, skipping: ${subBlock.type}`);
      return;
    }
    const rows: Recommendation[] = subBlock.rows.slice(0, visibleRows);
    return (
      <AccordionListingContainer
        rows={rows}
        renderUrlContent={renderUrlContent}
        hideGrade={isGradeHidden}
        campusId={parseInt(campus_id)}
        irvingtonCampusId={18}
        irvingtonUnblockCount={3}
      />
    );
  });

  const subject = studentLearningBlock.subject;
  const isEligibleForTestOut = studentLearningBlock.isEligibleForTest ?? false;

  const SubjectTitleHeader = (
    <SubjectTitleContainer
      sx={{
        flexDirection: !collapseClick ? 'column' : 'row',
      }}
    >
      <Typography key="title" variant="h4">
        {subject}{' '}
        {isEligibleForTestOut &&
          (isStudent ? (
            <Button
              variant="contained"
              disabled={testOutInProgress}
              color="primary"
              sx={{ paddingX: '0px', minWidth: '96px', marginLeft: '24px' }}
              onClick={() => onClickTestOut(subject)}
            >
              Test Out
            </Button>
          ) : (
            <Tooltip title="Student can initiate the test-out process on their own Dash account.">
              <span>
                <Button
                  variant="contained"
                  disabled
                  color="primary"
                  sx={{ paddingX: '0px', minWidth: '96px', marginLeft: '24px' }}
                >
                  Test Out
                </Button>
              </span>
            </Tooltip>
          ))}
      </Typography>
      <RecommendationTitleSummary goalSummary={goalSummary} />
    </SubjectTitleContainer>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: 'inherit',
      }}
      key={blockIdx}
    >
      <Box
        component={Paper}
        sx={{
          padding: '24px',
          marginY: '12px',
          backgroundColor: 'white',
        }}
      >
        <AccordionProgressWrapper triggerChildren={SubjectTitleHeader}>
          <StudentProgressView
            showHeader={false}
            subject={subject}
            data={studentWeeklyStats[subject]}
            goalSummary={goalSummary}
            style={{
              minWidth: 'unset',
              padding: 0,
              margin: '0 6px 56px 0',
              gap: 40,
            }}
          />
          {/* {isValidSubject && (
          )} */}
        </AccordionProgressWrapper>

        {subLessons}
      </Box>
    </TableContainer>
  );
};

type Props = {
  masteryThresholds: AppSubjectGradeThreshold[];
  studentLearningActivities?: Block[];
  credentialLookup: CredentialLookup;
  visibleRows: number | undefined;
  videos: Videos;
  studentWeeklyStats: StudentWeeklyStats;
  studentPersonalGoal: SubjectLevelPersonalGoal;
  full_student_id: string;
};

const RecommendationTable = ({
  masteryThresholds,
  studentLearningActivities,
  credentialLookup,
  visibleRows,
  videos,
  studentWeeklyStats,
  studentPersonalGoal,
  full_student_id,
}: Props) => (
  <Box id="recommendation-table">
    {studentLearningActivities?.map((block, blockIdx) => (
      <RecommendationBlock
        key={blockIdx}
        studentLearningBlock={block}
        credentialLookup={credentialLookup}
        visibleRows={visibleRows}
        blockIdx={blockIdx}
        videos={videos}
        studentPersonalGoal={studentPersonalGoal}
        studentWeeklyStats={studentWeeklyStats}
        masteryThreshold={masteryThresholds?.find(
          (threshold) => threshold.subject.toLowerCase() === block.subject.toLowerCase()
        )}
        full_student_id={full_student_id}
      />
    ))}
  </Box>
);

export default RecommendationTable;
